import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './styles.css'
import { Button } from 'primereact/button'
import { CountryDto, PersonApi, PersonRegisterApi, PersonRegisterDto } from '../../api'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Loading } from '../loading/Loading'
import { authState } from '../../state/Auth.state'
import { appState } from '../../state/App.state'

const NULL_DATE = new Date('1800-01-01')

const validateIban = (value: string): boolean => {
    const raw = value.replace(/\s/g, '').toUpperCase()
    if (raw.length === 0) return false
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(raw)) return false
    const rearranged = raw.slice(4) + raw.slice(0, 4)
    const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))
    let remainder = 0
    for (const chunk of numeric.match(/.{1,9}/g) ?? []) {
        remainder = Number(String(remainder) + chunk) % 97
    }
    return remainder === 1
}

const genderOptions = [
    { label: 'Frau', value: 'F' },
    { label: 'Herr', value: 'M' },
    { label: 'Divers', value: 'D' },
]

const EMPTY_PERSON_REGISTER: PersonRegisterDto = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: '',
    countryId: null,
    zipCode: '',
    city: '',
    street: '',
    birthday: NULL_DATE,
    membershipName: '',
    remark: '',
    gender: '',
    bankAccountIBAN: '',
    bankOwner: '',
}

const membershipOptions = [
    { label: 'Persönliches IGV Mitglied', value: 'IGV' },
    { label: 'PFAE', value: 'PFAE' },
    { label: 'MFT', value: 'MFT' },
    { label: 'Sleipnir', value: 'Sleipnir' },
]

const parseDate = (dd: string, mm: string, yyyy: string): Date => {
    const day = Number(dd)
    const month = Number(mm)
    const year = Number(yyyy)
    if (!day || !month || !year || yyyy.length < 4) return NULL_DATE
    const date = new Date(year, month - 1, day)
    date.setFullYear(year)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return NULL_DATE
    return date
}

export const Register = () => {
    /* STATE */
    const [loading, setLoading] = useState<boolean>(false)
    const [form, setForm] = useState<PersonRegisterDto>(EMPTY_PERSON_REGISTER)
    const [countries, setCountries] = useState<CountryDto[]>([])
    const [bdDay, setBdDay] = useState('')
    const [bdMonth, setBdMonth] = useState('')
    const [bdYear, setBdYear] = useState('')
    const refMonth = useRef<HTMLInputElement>(null)
    const refYear = useRef<HTMLInputElement>(null)

    /* EFFECTS */
    useEffect(() => {
        new PersonApi(authState.apicfg())
            .apiPersonCountriesGet()
            .then((list) => {
                const sorted = [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                setCountries(sorted)
            })
            .catch(() => {})
    }, [])

    /* DERIVED STATE */
    const ibanValid = useMemo(() => validateIban(form.bankAccountIBAN ?? ''), [form.bankAccountIBAN])

    const formValid = useMemo(() => {
        if (!form?.gender || form?.gender?.length < 1) return false
        if (!form?.firstName || form?.firstName?.length < 1) return false
        if (!form?.lastName || form?.lastName?.length < 1) return false
        if (!form?.email || form?.email?.length < 1) return false
        if (!form?.phoneNumber || form?.phoneNumber?.length < 1) return false
        if (!form?.birthday || form?.birthday <= NULL_DATE) return false
        if (!form?.country || form?.country?.length < 1) return false
        if (!form?.zipCode || form?.zipCode?.length < 1) return false
        if (!form?.city || form?.city?.length < 1) return false
        if (!form?.street || form?.street?.length < 1) return false
        if (!ibanValid) return false
        if (!form?.bankOwner || form?.bankOwner?.length < 1) return false
        return true
    }, [form, ibanValid])

    /* LOGIC */
    const register = useCallback((event?: React.SyntheticEvent) => {
        setLoading(true)
        new PersonRegisterApi(authState.apicfg())
            .apiPersonRegisterPost({ personRegisterDto: form })
            .then(() => {
                appState.addToast('Registrierung erfolgreich! Bitte prüfen Sie Ihr E-Mail-Postfach.', 'success')
                window.location.hash = '#/home'
            })
            .catch((err) => {
                appState.addToast('Registrierung fehlgeschlagen!', 'error')
                console.error(err)
            })
            .finally(() => setLoading(false))
    }, [form])

    /* TEMPLATE */
    return (
        <>
            <Loading loading={loading} />
            <div className='register-container'>
                <div className='register-form card'>
                    <h2 style={{ marginBottom: '1rem' }}>Person Registrieren</h2>
                    <form
                        onSubmit={(e) => { e.preventDefault(); register(e) }}
                        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                    >
                        {/* Anrede */}
                        <div style={{ display: 'flex' }}>
                            <Dropdown
                                style={{ flex: 1 }}
                                placeholder='Anrede *'
                                value={form.gender || null}
                                options={genderOptions}
                                optionLabel='label'
                                optionValue='value'
                                onChange={(e) => setForm((v) => ({ ...v, gender: e.value ?? '' }))}
                            />
                        </div>
                        {/* Vorname & Nachname */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <InputText
                                style={{ flex: 1, minWidth: '160px' }}
                                placeholder='Vorname *'
                                value={form.firstName}
                                onChange={(e) => setForm((v) => ({ ...v, firstName: e.target.value ?? '' }))}
                            />
                            <InputText
                                style={{ flex: 1, minWidth: '160px' }}
                                placeholder='Nachname *'
                                value={form.lastName}
                                onChange={(e) => setForm((v) => ({ ...v, lastName: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* E-Mail & Telefon */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <InputText
                                style={{ flex: 2, minWidth: '200px' }}
                                placeholder='E-Mail *'
                                value={form.email}
                                onChange={(e) => setForm((v) => ({ ...v, email: e.target.value ?? '' }))}
                            />
                            <InputText
                                style={{ flex: 1, minWidth: '160px' }}
                                placeholder='Tel. / Mobil *'
                                value={form.phoneNumber}
                                onChange={(e) => setForm((v) => ({ ...v, phoneNumber: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* Geburtstag */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <InputText
                                    style={{ width: '3.5rem', textAlign: 'center' }}
                                    placeholder='TT'
                                    maxLength={2}
                                    value={bdDay}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                                        setBdDay(val)
                                        if (val.length === 2) refMonth.current?.focus()
                                        setForm((v) => ({ ...v, birthday: parseDate(val, bdMonth, bdYear) }))
                                    }}
                                />
                                <span style={{ color: '#888' }}>.</span>
                                <InputText
                                    style={{ width: '3.5rem', textAlign: 'center' }}
                                    placeholder='MM'
                                    maxLength={2}
                                    value={bdMonth}
                                    ref={refMonth}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                                        setBdMonth(val)
                                        if (val.length === 2) refYear.current?.focus()
                                        setForm((v) => ({ ...v, birthday: parseDate(bdDay, val, bdYear) }))
                                    }}
                                />
                                <span style={{ color: '#888' }}>.</span>
                                <InputText
                                    style={{ width: '5rem', textAlign: 'center' }}
                                    placeholder='JJJJ'
                                    maxLength={4}
                                    value={bdYear}
                                    ref={refYear}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                                        setBdYear(val)
                                        setForm((v) => ({ ...v, birthday: parseDate(bdDay, bdMonth, val) }))
                                    }}
                                />
                                <span style={{ color: '#aaa', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Geburtstag *</span>
                            </div>
                        </div>
                        {/* Land & PLZ */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <Dropdown
                                style={{ flex: 1 }}
                                placeholder='Land *'
                                value={form.country || null}
                                options={countries}
                                optionLabel='name'
                                optionValue='code'
                                filter
                                onChange={(e) => {
                                    const selected = countries.find(c => c.code === e.value)
                                    setForm((v) => ({ ...v, country: e.value ?? '', countryId: selected?.id ?? null }))
                                }}
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='PLZ *'
                                value={form.zipCode}
                                onChange={(e) => setForm((v) => ({ ...v, zipCode: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* Stadt & Straße */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Stadt *'
                                value={form.city}
                                onChange={(e) => setForm((v) => ({ ...v, city: e.target.value ?? '' }))}
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Straße *'
                                value={form.street}
                                onChange={(e) => setForm((v) => ({ ...v, street: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* Vereinszugehörigkeit */}
                        <div style={{ display: 'flex' }}>
                            <Dropdown
                                style={{ flex: 1 }}
                                placeholder='Vereinszugehörigkeit auswählen (falls vorhanden)'
                                value={form.membershipName}
                                options={membershipOptions}
                                optionLabel='label'
                                optionValue='value'
                                onChange={(e) => setForm((v) => ({ ...v, membershipName: e.value ?? '' }))}
                                showClear
                            />
                        </div>
                        {/* Anmerkung */}
                        <div style={{ display: 'flex' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Anmerkung'
                                value={form.remark}
                                onChange={(e) => setForm((v) => ({ ...v, remark: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* Freizeitreiter */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type='checkbox'
                                id='leisureStatus'
                                checked={form.leisureStatus ?? false}
                                onChange={(e) => setForm((v) => ({ ...v, leisureStatus: e.target.checked }))}
                            />
                            <label htmlFor='leisureStatus'>Freizeitreiter gemäß IGV Reglement</label>
                        </div>
                        {/* IBAN & Kontoinhaber */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div style={{ flex: 2, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <InputText
                                    placeholder='IBAN *'
                                    value={form.bankAccountIBAN ?? ''}
                                    onChange={(e) => setForm((v) => ({ ...v, bankAccountIBAN: e.target.value ?? '' }))}
                                    style={{ width: '100%', borderColor: form.bankAccountIBAN ? (ibanValid ? '' : 'red') : '' }}
                                />
                                {form.bankAccountIBAN && !ibanValid && (
                                    <small style={{ color: 'red' }}>Ungültige IBAN</small>
                                )}
                            </div>
                            <InputText
                                style={{ flex: 1, minWidth: '160px' }}
                                placeholder='Kontoinhaber *'
                                value={form.bankOwner ?? ''}
                                onChange={(e) => setForm((v) => ({ ...v, bankOwner: e.target.value ?? '' }))}
                            />
                        </div>
                        <Button disabled={!formValid} type="submit">
                            Person Registrieren
                        </Button>
                        <br />
                        <Button type="button" outlined onClick={() => (window.location.hash = '#/home')}>
                            Zurück
                        </Button>
                    </form>
                </div>
            </div>
        </>
    )
}
